import { Observable } from 'rxjs';

import { AuditComment, AuditCommentsResponse } from '../../../interfaces/audit-comment.interface';
import {
  CfeTariffList,
  CreateQuotePayload,
  GetQuotesParams,
  PatchQuotePayload,
  PostalCodeResponse,
  Quote,
  QuoteLeadRef,
  QuoteLock,
  QuoteOptions,
  QuoteOptionsLocation,
  QuotesResponse,
  UtilityBillConfirmation,
  UtilityBillUpload,
  UtilityBillUploadPayload,
} from '../../../interfaces/quote.interface';
import { EntityTag } from '../../../interfaces/tag.interface';

/**
 * Único punto de acceso a datos de cotizaciones (`/quotes`). Clase abstracta
 * por la misma razón que `MailingApiService`: es el token de inyección.
 */
export abstract class QuotesApiService {
  abstract list(filters: GetQuotesParams): Observable<QuotesResponse>;

  abstract get(id: number): Observable<Quote>;

  /** Crea en `draft` y toma el bloqueo para quien crea. */
  abstract create(body: CreateQuotePayload): Observable<Quote>;

  /** Por sección. 409 `QUOTE_LOCKED` si la edita otra persona, `QUOTE_CLOSED` si está cerrada. */
  abstract patch(id: number, sections: PatchQuotePayload): Observable<Quote>;

  /** Toma o renueva el bloqueo de edición. */
  abstract acquireLock(id: number): Observable<QuoteLock>;

  /** Suelta el bloqueo; soltar el de otra persona exige `unlock_quotes`. */
  abstract releaseLock(id: number): Observable<void>;

  /**
   * Lo que el editor necesita para pintarse (segmentos, esquemas, permisos). Con la
   * ubicación del sitio marca qué esquemas tienen cobertura ahí.
   */
  abstract options(location?: QuoteOptionsLocation): Observable<QuoteOptions>;

  /** Colonias de un código postal, con su cobertura. */
  abstract postalCode(zip: string): Observable<PostalCodeResponse>;

  /** Tarifas CFE que aplican a un mes (`YYYY-MM`); sin mes, el en curso. */
  abstract cfeTariffs(period?: string): Observable<CfeTariffList>;

  /** Reserva el recibo de CFE y devuelve la URL prefirmada para subirlo a S3. */
  abstract uploadUtilityBill(
    id: number,
    body: UtilityBillUploadPayload,
  ): Observable<UtilityBillUpload>;

  /** Confirma que el archivo ya está en S3; `pending` si aún no se procesa. */
  abstract confirmUtilityBill(id: number, fileId: number): Observable<UtilityBillConfirmation>;

  abstract deleteUtilityBill(id: number, fileId: number): Observable<void>;

  /** Liga un lead a una cotización que no tiene; 409 si ya tiene uno. */
  abstract linkLead(id: number, leadId: number): Observable<{ lead: QuoteLeadRef }>;

  /**
   * Mueve la probabilidad de cierre con su justificación. No pide el bloqueo de
   * edición; 409 `QUOTE_CLOSED` si está cerrada. Regresa la cotización con su historial.
   */
  abstract setCloseProbability(id: number, pct: number, comment: string): Observable<Quote>;

  /** Deja exactamente esas etiquetas; requiere `manage_quotes` y no pide el bloqueo de edición. */
  abstract setTags(id: number, tagIds: number[]): Observable<{ tags: EntityTag[] }>;

  /** Del más nuevo al más viejo; `before` es el `nextBefore` de la página previa. */
  abstract listComments(id: number, before?: number): Observable<AuditCommentsResponse>;

  abstract addComment(id: number, comment: string): Observable<AuditComment>;
}
